import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckCircle2, Loader2, Play, Webhook, Calendar, Target, BookOpen, ArrowRight,
  Plus, X, RotateCcw, Move,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import ErrorState from "../components/ErrorState";
import Logo from "../components/Logo";
import ClaireHelpButton from "../components/ClaireHelpButton";
import { US_STATES, US_CITIES_BY_STATE, stateCodeFromCity } from "../data/us_locations";
import { resetSidebarPrefs } from "../hooks/useSidebarPrefs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SectionCard({ icon: Icon, title, subtitle, children }) {
  return (
    <section className="al-card p-5">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-[#EFF6FF] text-[#3B82F6] flex items-center justify-center shrink-0">
          <Icon className="w-[18px] h-[18px]" />
        </div>
        <div>
          <h3 className="text-[15px] font-bold text-[#111827]">{title}</h3>
          {subtitle && <p className="text-[12px] text-[#6B7280] mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

export default function Config() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [businessType, setBusinessType] = useState("");
  const [city, setCity] = useState("");
  // Iter99 — separate state filter so any US state (not just FL) can be picked.
  const [stateCode, setStateCode] = useState("FL");
  const [businessTypes, setBusinessTypes] = useState([]);
  const [builtinSet, setBuiltinSet] = useState(new Set());
  const [error, setError] = useState(null);
  // Iter99 — manual category add ("+") modal state.
  const [showAddCat, setShowAddCat] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [savingCat, setSavingCat] = useState(false);

  // Cities list resolves from the picked state. Empty/unknown state → FL list.
  const stateCities = US_CITIES_BY_STATE[stateCode] || [];

  const load = () => {
    setLoading(true);
    setError(null);
    Promise.all([
      axios.get(`${API}/config`),
      axios.get(`${API}/business-types`),
    ]).then(([cfg, bt]) => {
      setConfig(cfg.data);
      setBusinessType(cfg.data.target?.business_type || "");
      const initCity = cfg.data.target?.city || "";
      setCity(initCity);
      // Derive state from "City, ST" — falls back to current FL default.
      const s = stateCodeFromCity(initCity);
      if (s) setStateCode(s);
      setBusinessTypes(bt.data.types || []);
      setBuiltinSet(new Set((bt.data.builtin || []).map((s) => s.toLowerCase())));
      setLoading(false);
    }).catch((e) => {
      setError(e?.message || "Falha ao carregar configurações");
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  // Iter99 — add a new business category manually via the "+" button.
  const addCategory = async () => {
    const name = newCatName.trim();
    if (name.length < 2) {
      toast.error("Nome muito curto");
      return;
    }
    setSavingCat(true);
    try {
      const { data } = await axios.post(`${API}/business-types`, { name });
      const added = data?.type?.name || name;
      setBusinessTypes((prev) => prev.includes(added) ? prev : [...prev, added]);
      setBusinessType(added);
      setNewCatName("");
      setShowAddCat(false);
      toast.success(`"${added}" adicionado às categorias`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Falha ao adicionar");
    } finally {
      setSavingCat(false);
    }
  };

  const removeCategory = async (name) => {
    if (builtinSet.has(name.toLowerCase())) {
      toast.error("Categoria nativa não pode ser removida");
      return;
    }
    if (!window.confirm(`Remover "${name}"?`)) return;
    try {
      await axios.delete(`${API}/business-types/${encodeURIComponent(name)}`);
      setBusinessTypes((prev) => prev.filter((t) => t !== name));
      if (businessType === name) setBusinessType("");
      toast.success("Categoria removida");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const executar = () => {
    const params = new URLSearchParams();
    if (businessType) params.set("business_type", businessType);
    if (city) params.set("location", city);
    params.set("auto", "1");
    navigate(`/prospecting?${params.toString()}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB]">
        <Sidebar />
        <div className="ml-[var(--al-sidebar-w,260px)] flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
        </div>
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="min-h-screen bg-[#F9FAFB]">
        <Sidebar />
        <div className="ml-[var(--al-sidebar-w,260px)] px-8 py-16">
          <ErrorState
            testid="config-error"
            title="Não foi possível carregar as configurações"
            message={error || "Verifique sua conexão e tente novamente."}
            onRetry={load}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <Sidebar />
      <div className="ml-[var(--al-sidebar-w,260px)]">
        <header
          data-testid="config-header"
          className="sticky top-0 z-30 bg-white border-b border-[#E5E7EB] px-8 py-4 flex items-center justify-between gap-4"
        >
          <div className="flex items-center gap-4">
            <Logo testid="config-logo-icon" className="w-9 h-9 object-contain" />
            <div>
              <h1 className="text-[16px] font-semibold text-[#111827]">
                ⚙️ Configurações do Agente
              </h1>
              <p className="text-[12.5px] text-[#6B7280] mt-0.5">
                Aba automatizada — sincronizada com a planilha Config
              </p>
            </div>
          </div>
          <ClaireHelpButton
            testid="config"
            question="Como configuro corretamente o Auto Pilot — filtros de público-alvo, cidades e categoria?"
          />
        </header>

        <main className="px-8 py-6 max-w-[1400px] mx-auto space-y-5">
          {/* Status banner */}
          <div
            data-testid="config-status-banner"
            className="al-card p-5 flex flex-wrap items-center justify-between gap-4"
          >
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-[#DCFCE7] text-[#16A34A] flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold">
                  Status do Agente
                </div>
                <div className="text-[14px] font-bold text-[#111827]">
                  {config.agent.status} <span className="text-[#6B7280] font-medium">— {config.agent.summary}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-6 text-[12.5px]">
              <div className="flex items-center gap-2 text-[#6B7280]">
                <Calendar className="w-4 h-4" />
                <span>Atualizado em <strong className="text-[#111827]" data-testid="config-updated-at">{new Date().toLocaleDateString("pt-BR")}</strong></span>
              </div>
              <a
                href={config.agent.webhook_token}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="webhook-link"
                className="flex items-center gap-2 text-[#3B82F6] hover:underline truncate max-w-[260px]"
                title={config.agent.webhook_token}
              >
                <Webhook className="w-4 h-4 shrink-0" />
                <span className="truncate">Webhook Token</span>
              </a>
            </div>
          </div>

          {/* Target row — standalone now. Sources / Tools / Platforms /
              Agencies were relocated to the Library (folder view) in Iter39. */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <SectionCard
              icon={Target}
              title="🎯 Alvo de Negócio"
              subtitle="Define quem o agente vai prospectar agora"
            >
              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold">
                      Tipo de Negócio
                    </label>
                    <button
                      type="button"
                      data-testid="config-add-category-btn"
                      onClick={() => setShowAddCat(true)}
                      title="Adicionar nova categoria"
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-[#3B82F6]/30 bg-[#EFF6FF] text-[#1D4ED8] text-[10.5px] font-bold uppercase tracking-wide hover:bg-[#DBEAFE] transition-colors"
                    >
                      <Plus className="w-3 h-3" /> Categoria
                    </button>
                  </div>
                  <select
                    data-testid="config-business-type-input"
                    value={businessType}
                    onChange={(e) => setBusinessType(e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border border-[#E5E7EB] focus:outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#3B82F6]/15 text-[14px] bg-white text-[#111827]"
                    style={{ colorScheme: "light" }}
                  >
                    {businessTypes.length === 0 && (
                      <option value={businessType} className="bg-white text-[#111827]">{businessType || "Selecione…"}</option>
                    )}
                    {businessTypes.map((t) => (
                      <option key={t} value={t} className="bg-white text-[#111827]">{t}</option>
                    ))}
                  </select>
                  {/* Show user-added (non-builtin) categories with a delete chip so the user
                      can curate his/her custom list directly from this screen. */}
                  {businessTypes.filter((b) => !builtinSet.has(b.toLowerCase())).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="config-custom-categories">
                      {businessTypes.filter((b) => !builtinSet.has(b.toLowerCase())).map((cat) => (
                        <span key={cat} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#FEF3C7] border border-[#FCD34D] text-[#92400E] text-[10.5px] font-semibold">
                          {cat}
                          <button
                            type="button"
                            data-testid={`config-remove-cat-${cat}`}
                            onClick={() => removeCategory(cat)}
                            className="hover:text-[#7C2D12]"
                            title="Remover"
                          ><X className="w-3 h-3" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1">
                    Estado (EUA)
                  </label>
                  <select
                    data-testid="config-state-input"
                    value={stateCode}
                    onChange={(e) => {
                      const next = e.target.value;
                      setStateCode(next);
                      // Reset city when state changes so the user picks a valid one.
                      if (city && stateCodeFromCity(city) !== next) setCity("");
                    }}
                    className="w-full h-10 px-3 rounded-lg border border-[#E5E7EB] focus:outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#3B82F6]/15 text-[14px] bg-white text-[#111827]"
                    style={{ colorScheme: "light" }}
                  >
                    {US_STATES.map((s) => (
                      <option key={s.code} value={s.code} className="bg-white text-[#111827]">
                        {s.code} — {s.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1">
                    Cidade
                  </label>
                  {stateCities.length > 0 ? (
                    <select
                      data-testid="config-city-input"
                      value={stateCities.includes(city) ? city : ""}
                      onChange={(e) => setCity(e.target.value)}
                      className="w-full h-10 px-3 rounded-lg border border-[#E5E7EB] focus:outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#3B82F6]/15 text-[14px] bg-white text-[#111827]"
                      style={{ colorScheme: "light" }}
                    >
                      <option value="" className="bg-white text-[#111827]">Selecione uma cidade…</option>
                      {stateCities.map((c) => (
                        <option key={c} value={c} className="bg-white text-[#111827]">{c}</option>
                      ))}
                      <option value="__custom__" className="bg-white text-[#3B82F6]">Outra cidade…</option>
                    </select>
                  ) : (
                    <input
                      data-testid="config-city-input-free"
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder={`Ex: Cidade, ${stateCode}`}
                      className="w-full h-10 px-3 rounded-lg border border-[#E5E7EB] focus:outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#3B82F6]/15 text-[14px] bg-white text-[#111827]"
                      style={{ colorScheme: "light" }}
                    />
                  )}
                  {/* If user picked __custom__ above, surface a free-text input as well. */}
                  {city === "__custom__" && (
                    <input
                      data-testid="config-city-custom-input"
                      type="text"
                      autoFocus
                      onChange={(e) => setCity(e.target.value ? `${e.target.value}, ${stateCode}` : "")}
                      placeholder={`Digite a cidade (será preenchida com ", ${stateCode}")`}
                      className="mt-2 w-full h-10 px-3 rounded-lg border border-[#3B82F6] focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/15 text-[14px] bg-white text-[#111827]"
                      style={{ colorScheme: "light" }}
                    />
                  )}
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1">
                    Motivo da Busca
                  </label>
                  <p className="text-[13px] text-[#374151]">{config.target.reason}</p>
                </div>
                <button
                  onClick={executar}
                  data-testid="config-executar-btn"
                  className="al-btn-primary w-full mt-2 flex items-center justify-center gap-2 h-10 rounded-lg font-semibold text-[13px]"
                >
                  <Play className="w-4 h-4" /> EXECUTAR AGORA
                </button>
              </div>
            </SectionCard>

            <div className="lg:col-span-2 al-card p-6 flex items-center justify-between gap-6 bg-gradient-to-br from-[#EFF6FF] to-white border-[#DBEAFE]" data-testid="config-library-cta">
              <div className="flex items-start gap-4 flex-1 min-w-0">
                <div className="w-12 h-12 rounded-xl bg-[#3B82F6] text-white flex items-center justify-center shrink-0">
                  <BookOpen className="w-6 h-6" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-[15px] font-bold text-[#111827]">Movemos Fontes, Ferramentas, Plataformas e Agências para a Biblioteca</h3>
                  <p className="text-[12.5px] text-[#6B7280] mt-1 leading-relaxed">
                    Agora você encontra tudo organizado em pastas: <strong>📡 Fontes Ativas</strong>, <strong>🛠 Ferramentas de Terceiros</strong>, <strong>🏠 Plataformas · Prestadores</strong> e <strong>🤝 Agências B2B</strong>. Mais fácil de navegar e auditar.
                  </p>
                </div>
              </div>
              <Link to="/library" className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[13px] font-bold shrink-0">
                Abrir Biblioteca <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            {/* Iter337 · Sidebar preferences · Restore default order */}
            <SidebarPreferencesCard />
          </div>
        </main>
      </div>

      {/* Iter99 — Add category modal */}
      {showAddCat && (
        <div
          data-testid="config-add-cat-modal"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/55 backdrop-blur-sm px-4"
          onClick={() => setShowAddCat(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="text-[16px] font-bold text-[#111827]">Adicionar Categoria</h3>
                <p className="text-[12px] text-[#6B7280] mt-0.5">A nova categoria fica disponível para todos os usuários da conta.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowAddCat(false)}
                className="text-[#6B7280] hover:text-[#111827]"
                data-testid="config-add-cat-close"
              ><X className="w-5 h-5" /></button>
            </div>
            <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1">
              Nome da Categoria
            </label>
            <input
              data-testid="config-add-cat-input"
              type="text"
              autoFocus
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !savingCat) addCategory(); }}
              placeholder="Ex: HVAC Repair, Solar Installation…"
              className="w-full h-10 px-3 rounded-lg border border-[#E5E7EB] focus:outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#3B82F6]/15 text-[14px] bg-white text-[#111827]"
              style={{ colorScheme: "light" }}
            />
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowAddCat(false)}
                className="h-10 px-4 rounded-lg border border-[#E5E7EB] text-[13px] font-semibold text-[#374151] hover:bg-[#F3F4F6]"
                data-testid="config-add-cat-cancel"
              >Cancelar</button>
              <button
                type="button"
                onClick={addCategory}
                disabled={savingCat || newCatName.trim().length < 2}
                className="h-10 px-4 rounded-lg bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[13px] font-bold inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="config-add-cat-save"
              >
                {savingCat ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Adicionar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Iter337 · Sidebar preferences card — shows the "Restore default
// sidebar order" button and a brief description of the drag-and-drop
// customization. Rendered inside Config's main grid.
function SidebarPreferencesCard() {
  const [resetting, setResetting] = useState(false);
  const [done, setDone] = useState(false);

  const restore = async () => {
    setResetting(true);
    try {
      const ok = await resetSidebarPrefs();
      if (ok) {
        setDone(true);
        toast.success("Ordem do menu restaurada. Recarregando…", { duration: 1200 });
        setTimeout(() => window.location.reload(), 900);
      } else {
        toast.error("Falha ao restaurar. Tente novamente.");
      }
    } finally { setResetting(false); }
  };

  return (
    <div className="lg:col-span-2 al-card p-6 flex items-center justify-between gap-6 bg-gradient-to-br from-[#F4F7FC] to-white border-[#DBEAFE]"
         data-testid="config-sidebar-prefs">
      <div className="flex items-start gap-4 flex-1 min-w-0">
        <div className="w-12 h-12 rounded-xl bg-[#1B3A8A] text-white flex items-center justify-center shrink-0">
          <Move className="w-6 h-6" />
        </div>
        <div className="min-w-0">
          <h3 className="text-[15px] font-bold text-[#111827]">
            Personalização do menu lateral
          </h3>
          <p className="text-[12.5px] text-[#6B7280] mt-1 leading-relaxed">
            Você pode arrastar e reordenar os itens do menu lateral (ícone de <b>seis pontos</b> aparece ao passar o mouse).
            Ou use <kbd className="px-1 rounded border bg-white text-[10.5px]">Alt</kbd>+<kbd className="px-1 rounded border bg-white text-[10.5px]">↑</kbd>/<kbd className="px-1 rounded border bg-white text-[10.5px]">↓</kbd>.
            A ordem fica salva por usuário — clique abaixo para voltar ao padrão do sistema.
          </p>
        </div>
      </div>
      <button type="button" onClick={restore}
        disabled={resetting || done}
        data-testid="config-restore-sidebar-order"
        className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg bg-white hover:bg-[#EAF1FC] text-[#1B3A8A] border border-[#1B3A8A]/30 text-[13px] font-bold shrink-0 disabled:opacity-60">
        {resetting
          ? <Loader2 className="w-4 h-4 animate-spin" />
          : <RotateCcw className="w-4 h-4" />}
        {done ? "Restaurado" : "Restaurar ordem padrão"}
      </button>
    </div>
  );
}
